/**
 * Keep a repeated diagnostic from being silently discarded by Sentry's
 * `Dedupe` integration — CU-86akbg4qm.
 *
 * ── THE PROBLEM (MEASURED LIVE 2026-09-24, P2b) ───────────────────────────
 *
 * `Dedupe` (a DEFAULT integration of `@sentry/browser`) drops any event that
 * matches the PREVIOUSLY CAPTURED event on message + fingerprint + stacktrace
 * (`@sentry/core` `integrations/dedupe.js`). Tags and `extra` are NOT compared,
 * and a `captureMessage` carries no stacktrace — so two consecutive
 * "mfa challenge: resolve failed" events are "the same" however different
 * their codes or timings. The previous event is recorded when CAPTURED, not
 * when delivered, so this happens online too.
 *
 * Measured: two wrong codes in a row, online → ONE event delivered, and the
 * SDK's own client report said `{"reason":"event_processor","category":"error",
 * "quantity":1}`. This — not the network — is why the original 2026-09-03
 * report saw one event for two failures.
 *
 * ── THE FIX: VARY THE MESSAGE, PIN THE FINGERPRINT ────────────────────────
 *
 * Callers pass a STABLE `fingerprint`, which decides the Sentry ISSUE — so
 * every event still lands in one issue with a running count. The message,
 * which only titles the individual event, gains a sequence number from the
 * second occurrence on. Varying the fingerprint instead would split the issue
 * per event; varying nothing loses the event.
 *
 * ⚠️ THE COUNTER IS MODULE-LEVEL — PER PAGE — ON PURPOSE. Dedupe's memory is
 * page-wide, not per hook instance. A per-hook counter would restart at 1 when
 * the challenge form remounts ("start over"), and two consecutive first
 * attempts would collide again.
 *
 * ⚠️ A mocked `Sentry` CANNOT observe Dedupe — it runs inside the real client.
 * Unit tests pin that the messages differ; only a live run (repeat P2b) proves
 * delivery.
 */

const sequences = new Map<string, number>();

/**
 * The message to capture for this occurrence of `base`: unchanged the first
 * time in this page, `"<base> (#n)"` after that.
 */
export function dedupeSafeMessage(base: string): string {
  const n = (sequences.get(base) ?? 0) + 1;
  sequences.set(base, n);
  return n === 1 ? base : `${base} (#${n})`;
}

/** Exported for tests — module state would otherwise leak between them. */
export function __resetDedupeSequencesForTest(): void {
  sequences.clear();
}
